import { ref, watch } from '@vue/composition-api';
import { giftAPI } from '../utils/api.js';
import { getSharedAssistantSession } from './sharedAssistantSession.js';

export function useGiftSearch() {
  const giftSuggestions = ref([]);
  const isSearchingGifts = ref(false);
  const currentPage = ref(1);
  const totalPages = ref(1);
  const hasNextPage = ref(false);
  const currentSearchQuery = ref('');
  const showDefaultGiftPreview = ref(true);
  const itemOrderCounter = ref(0);
  const resultCursor = ref(null);

  const filterDuplicates = (items) => {
    const seen = new Set();
    return items.filter((item) => {
      if (!item.productId) return true;
      if (seen.has(item.productId)) return false;
      seen.add(item.productId);
      return true;
    });
  };

  const searchGifts = async (searchText, page = 1) => {
    if (isSearchingGifts.value) return;

    // Avoid repeat fetch for same query/page
    if (
      giftSuggestions.value.length > 0 &&
      currentSearchQuery.value === searchText &&
      currentPage.value === page
    ) {
      return;
    }

    const cleanSearchText = (searchText || '').trim().toLowerCase();
    if (!cleanSearchText) return;
    if (currentSearchQuery.value !== cleanSearchText) {
      resultCursor.value = null;
    }

    try {
      isSearchingGifts.value = true;
      const response = await giftAPI.searchGifts(
        cleanSearchText,
        page,
        resultCursor.value,
      );

      if (response.status && response.response?.item) {
        const items = filterDuplicates(response.response.item).map((item) => ({
          ...item,
          searchQuery: cleanSearchText,
          orderIndex: itemOrderCounter.value++,
        }));

        if (response.response.pagination) {
          currentPage.value = response.response.pagination.current_page;
          totalPages.value = response.response.pagination.max_page;
          hasNextPage.value = response.response.pagination.has_next_page;
          resultCursor.value =
            response.response.pagination.result_cursor || null;
        }

        currentSearchQuery.value = cleanSearchText;
        giftSuggestions.value = items;
        showDefaultGiftPreview.value = false;
      } else {
        // No items returned
        giftSuggestions.value = [
          {
            searchQuery: cleanSearchText,
            error: true,
            message: `No gift suggestions found for "${cleanSearchText}".`,
          },
        ];
        showDefaultGiftPreview.value = false;
      }
    } catch (error) {
      console.error('Gift search failed:', error);

      giftSuggestions.value = [
        {
          searchQuery: searchText,
          error: true,
          message: 'Failed to load gift suggestions. Please try again.',
        },
      ];
      showDefaultGiftPreview.value = false;
    } finally {
      isSearchingGifts.value = false;
    }
  };

  const clearGiftSuggestions = () => {
    giftSuggestions.value = [];
    currentPage.value = 1;
    totalPages.value = 1;
    hasNextPage.value = false;
    currentSearchQuery.value = '';
    showDefaultGiftPreview.value = true;
    itemOrderCounter.value = 0;
    resultCursor.value = null;
  };

  const nextPage = () => {
    if (hasNextPage.value && currentSearchQuery.value) {
      searchGifts(currentSearchQuery.value, currentPage.value + 1);
    }
  };

  const previousPage = () => {
    if (currentPage.value > 1 && currentSearchQuery.value) {
      searchGifts(currentSearchQuery.value, currentPage.value - 1);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages.value && currentSearchQuery.value) {
      searchGifts(currentSearchQuery.value, page);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages.value <= maxVisiblePages) {
      for (let i = 1; i <= totalPages.value; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, currentPage.value - Math.floor(maxVisiblePages / 2));
      let end = Math.min(totalPages.value, start + maxVisiblePages - 1);

      if (end === totalPages.value) {
        start = Math.max(1, end - maxVisiblePages + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  };

  const openGiftLink = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  // The session composable's runSearchGifts populates session.gifts when the
  // model calls search_gifts. Mirror those into giftSuggestions (UI grid)
  // and mirror session.isFetchingGifts into isSearchingGifts (UI loader).
  // Single fetch, single source of truth with deduplicated watcher registration.
  try {
    const session = getSharedAssistantSession();
    if (session && session.gifts && !session._watchersBound) {
      session._watchersBound = true;
      watch(session.gifts, (items) => {
        if (Array.isArray(items)) {
          giftSuggestions.value = items;
          showDefaultGiftPreview.value = items.length === 0 ? showDefaultGiftPreview.value : false;
        }
      });
      if (session.isFetchingGifts) {
        watch(session.isFetchingGifts, (fetching) => {
          isSearchingGifts.value = !!fetching;
        });
      }
    }
  } catch (e) {
    console.warn('useGiftSearch session bridge failed:', e);
  }

  return {
    // State
    giftSuggestions,
    isSearchingGifts,
    currentPage,
    totalPages,
    hasNextPage,
    currentSearchQuery,
    showDefaultGiftPreview,
    itemOrderCounter,

    // Methods
    searchGifts,
    clearGiftSuggestions,
    nextPage,
    previousPage,
    goToPage,
    getPageNumbers,
    openGiftLink,
  };
}
